import { Box, Checkbox, FormControlLabel } from "@mui/material";
import React from "react";

import { SelectedCoverage, FormState, CoverageOption } from "../types";

import CensusRadio from "./census-radio";
import {
  COVERED_COVERAGES,
  DUAL_OPTION,
  EXISTING,
  NEW,
  YES_NO_OPTIONS,
  TERM_DISABILITY_COVERAGES,
  YES,
  BENEFIT_SCTRUCTURE,
  BENEFIT_SCTRUCTURE_OPTIONS,
  SECOND_QUESTION_LABELS,
  YES_DASH_OPTIONS,
  FULL_COVERED_CLASSES,
  COVERAGE_QUESTIONNAIRE,
} from "./constants";
import CensusExistingCoverageQuestionnaire from "./census-existing-coverage-questionnaire";
import CensusClassesSelect from "./census-classes-select";

interface ICensusCheckboxProps {
  label: string;
  coverageName: string;
  selectedCoverages: SelectedCoverage;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onChangeOptions?: (
    coverage: string,
    name: string,
    value: string | { value?: string } | CoverageOption,
  ) => void;
  hideQuestions?: boolean;
  disabled?: boolean;
  groupClasses?: string[];
  updateFormStateValue?: <T extends keyof FormState>(
    key: T,
    value: FormState[T],
  ) => void;
}

const CensusCheckbox = (props: ICensusCheckboxProps) => {
  const {
    label,
    coverageName,
    selectedCoverages,
    onChange,
    onChangeOptions,
    hideQuestions = false,
    disabled = false,
    groupClasses = [],
    updateFormStateValue,
  } = props;

  const selectedCoverage = selectedCoverages[coverageName];
  const isChecked = !!selectedCoverage;
  const isCovered = COVERED_COVERAGES.includes(coverageName);
  const isTermDisability = TERM_DISABILITY_COVERAGES.includes(coverageName);

  return (
    <>
      <FormControlLabel
        control={
          <Checkbox
            checked={isChecked}
            onChange={onChange}
            name={coverageName}
            disabled={disabled}
          />
        }
        label={label}
      />
      {!hideQuestions && isChecked && (
        <Box ml={4} display="flex" flexDirection="column">
          <CensusRadio
            disabled={disabled}
            label="Is this existing coverage or new coverage?"
            name={EXISTING}
            value={selectedCoverages[coverageName]?.existing || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              onChangeOptions?.(coverageName, e.target.name, e.target.value);
            }}
            options={[
              { label: "Existing", value: EXISTING },
              { label: "New", value: NEW },
            ]}
          />
          {!isCovered &&
            !isTermDisability &&
            selectedCoverages[coverageName].existing === EXISTING && (
              <ExistingCoverageSecondStep
                coverageName={coverageName}
                onChangeOptions={onChangeOptions}
                disabled={disabled}
                selectedCoverages={selectedCoverages}
                isTermDisability={isTermDisability}
              />
            )}
          {isCovered && (
            <CensusRadio
              disabled={disabled}
              label="What is the benefit structure?"
              name={BENEFIT_SCTRUCTURE}
              value={selectedCoverages[coverageName]?.benefitStructure ?? ""}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                onChangeOptions?.(coverageName, e.target.name, e.target.value);
              }}
              options={BENEFIT_SCTRUCTURE_OPTIONS}
            />
          )}
          {isTermDisability &&
            selectedCoverages[coverageName].existing === EXISTING && (
              <>
                <CensusRadio
                  disabled={disabled}
                  label="Are any classes eligible for 100% employer paid coverage?"
                  name={FULL_COVERED_CLASSES}
                  value={
                    selectedCoverages[coverageName]?.fullCoveredClasses ?? ""
                  }
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    onChangeOptions?.(
                      coverageName,
                      e.target.name,
                      e.target.value,
                    );
                  }}
                  options={YES_NO_OPTIONS}
                />
                {selectedCoverages[coverageName].fullCoveredClasses === YES &&
                  groupClasses.length > 1 &&
                  updateFormStateValue && (
                    <CensusClassesSelect
                      options={groupClasses}
                      selectedCoverages={selectedCoverages}
                      coverageName={coverageName}
                      updateFormStateValue={updateFormStateValue}
                    />
                  )}
              </>
            )}
        </Box>
      )}
    </>
  );
};

interface IExistingCoverageSecondStep {
  coverageName: string;
  onChangeOptions?: (
    coverage: string,
    name: string,
    value: string | { value?: string } | CoverageOption,
  ) => void;
  disabled?: boolean;
  selectedCoverages: SelectedCoverage;
  isTermDisability: boolean;
}

const ExistingCoverageSecondStep = (props: IExistingCoverageSecondStep) => {
  const {
    coverageName,
    onChangeOptions,
    disabled = false,
    isTermDisability,
    selectedCoverages,
  } = props;
  const questionnaireData = COVERAGE_QUESTIONNAIRE[coverageName];

  if (questionnaireData) {
    return (
      <CensusExistingCoverageQuestionnaire
        options={questionnaireData}
        coverageName={coverageName}
        onChangeOptions={onChangeOptions}
        selectedCoverages={selectedCoverages}
      />
    );
  }

  return (
    <CensusRadio
      disabled={disabled}
      label={
        SECOND_QUESTION_LABELS?.[coverageName] ??
        "Is there a dual-option offering?"
      }
      name={DUAL_OPTION}
      value={selectedCoverages[coverageName]?.dualOption ?? ""}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
        onChangeOptions?.(coverageName, e.target.name, e.target.value);
      }}
      options={isTermDisability ? YES_NO_OPTIONS : YES_DASH_OPTIONS}
    />
  );
};

export default CensusCheckbox;
